import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Flame, Building2, PartyPopper, Droplets, Salad, Briefcase, Cpu, MapPin, Check, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { IMG, FAVOURITES } from "@/i18n";
import { PromoBanner } from "@/components/PromoBanner";

const EASE = [0.22, 1, 0.36, 1];
const FEATURE_ICONS = [Leaf, Flame, Building2, PartyPopper];
const WHY_ICONS = [Leaf, Droplets, Salad, Briefcase, Cpu, MapPin];
const CAT_IMG = { breakfast: IMG.catBreakfast, lunch: IMG.catLunch, corporate: IMG.catCorporate };

const Reveal = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

const YellowBtn = ({ children, onClick, to, testId, dark }) => {
  const cls = `inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold transition-all hover:shadow-lg ${
    dark ? "bg-navy text-cream hover:bg-pine" : "bg-brand text-navy hover:bg-branddark"
  }`;
  if (to) return <Link to={to} data-testid={testId} className={cls}>{children}<ArrowRight className="h-4 w-4" /></Link>;
  return <button onClick={onClick} data-testid={testId} className={cls}>{children}<ArrowRight className="h-4 w-4" /></button>;
};

export default function HomePage({ t, lang }) {
  const navigate = useNavigate();
  const goOrder = () => document.getElementById("order")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main data-testid="home-page">
      <Seo
        title="Kurkuma AI Kitchen — AI-Powered Healthy Indian Breakfast & Lunch in Warsaw"
        siteName="Kurkuma AI Kitchen"
        description="Authentic Indian food for modern Warsaw. Fresh, healthy, balanced and full of flavour. Breakfast, lunch, corporate catering — delivery only."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Restaurant",
          name: "Kurkuma AI Kitchen",
          servesCuisine: "Indian",
          address: { "@type": "PostalAddress", streetAddress: "Posing St. 7/11", addressLocality: "Warszawa", postalCode: "00-124", addressCountry: "PL" },
        }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy text-cream" data-testid="hero-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-14 md:px-8 lg:grid-cols-2 lg:py-20">
          <div>
            <motion.img
              src="/logo.png"
              alt="the Kurkuma — turmeric logo"
              initial={{ opacity: 0, scale: 0.6, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="mb-6 w-24 md:w-28"
              data-testid="hero-logo"
            />
            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
              className="font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-[3.4rem]"
              data-testid="hero-heading"
            >
              {t.hero.title1}
              <br />
              <span className="text-brand">{t.hero.title2}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
              className="mt-5 max-w-md text-base leading-relaxed text-cream/70 md:text-lg"
              data-testid="hero-subcopy"
            >
              {t.hero.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <YellowBtn onClick={goOrder} testId="hero-order-btn">{t.orderNow}</YellowBtn>
              <Link
                to="/catering"
                data-testid="hero-corporate-btn"
                className="inline-flex items-center gap-2 rounded-full border-2 border-cream/30 px-7 py-3 text-sm font-semibold text-cream transition-colors hover:border-brand hover:text-brand"
              >
                {t.hero.cta2}
              </Link>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: EASE, delay: 0.6 }}
            className="relative"
          >
            <img
              src={IMG.heroBowl}
              alt="Indian thali — rice, naan and assorted curries"
              className="mx-auto aspect-square w-full max-w-[520px] rounded-[2.5rem] border-4 border-brand/30 object-cover shadow-2xl"
              data-testid="hero-image"
            />
          </motion.div>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="border-b border-black/5 bg-paper" data-testid="feature-strip">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-8 md:px-8 lg:grid-cols-4">
          {t.features.map((f, i) => {
            const Icon = FEATURE_ICONS[i];
            return (
              <Reveal key={f.t} delay={i * 0.08} className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand/15 text-leaf">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-display text-sm font-semibold text-navy">{f.t}</p>
                  <p className="text-xs text-ink/50">{f.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-8" data-testid="category-cards">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {t.categories.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.1} data-testid={`category-card-${c.id}`}>
              <div className="group h-full overflow-hidden rounded-3xl bg-paper shadow-md transition-shadow duration-300 hover:shadow-xl">
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={CAT_IMG[c.id]}
                    alt={c.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-xl font-bold text-navy">{c.title}</h3>
                  <p className="mt-2 text-sm text-ink/60">{c.desc}</p>
                  <ul className="mt-4 space-y-1.5 text-[13px] font-medium text-ink/70">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-brand" /> {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6">
                    <YellowBtn to={c.id === "corporate" ? "/catering" : `/${c.id}`} testId={`category-cta-${c.id}`}>{c.cta}</YellowBtn>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <PromoBanner t={t} />

      {/* WHY KURKUMA */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-8" data-testid="why-section">
        <Reveal className="mb-12 text-center">
          <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">{t.why.title}</h2>
          <p className="mt-2 text-ink/60">{t.why.sub}</p>
        </Reveal>
        <div className="grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-6">
          {t.why.items.map((w, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <Reveal key={w.t} delay={i * 0.06} className="text-center" data-testid={`why-item-${i}`}>
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/15 text-leaf">
                  <Icon className="h-6 w-6" />
                </span>
                <p className="mt-3 font-display text-sm font-semibold text-navy">{w.t}</p>
                <p className="mt-1 text-xs leading-relaxed text-ink/50">{w.d}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FAVOURITE DISHES */}
      <section className="bg-paper py-16" data-testid="favourites-section">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">{t.favourites.title}</h2>
              <p className="mt-2 text-ink/60">{t.favourites.sub}</p>
            </div>
            <Link to="/menu" data-testid="favourites-view-all" className="text-sm font-semibold text-branddark transition-colors hover:text-navy">
              {t.favourites.viewAll} →
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-5">
            {FAVOURITES.map((d, i) => (
              <Reveal key={d.name.en} delay={i * 0.07} data-testid={`fav-dish-${i}`}>
                <div className="group overflow-hidden rounded-2xl bg-cream shadow-sm transition-shadow duration-300 hover:shadow-lg">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={d.img}
                      alt={d.name[lang]}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <p className="font-display text-sm font-semibold text-navy">{d.name[lang]}</p>
                    <p className="mt-0.5 text-sm font-bold text-branddark">{d.price}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CORPORATE + CATERING CARDS */}
      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-16 md:px-8 lg:grid-cols-2" data-testid="b2b-section">
        <Reveal>
          <div className="flex h-full flex-col overflow-hidden rounded-3xl bg-navy text-cream">
            <img src={IMG.corporateCard} alt="Corporate lunch boxes" loading="lazy" className="h-44 w-full object-cover opacity-80" />
            <div className="flex flex-1 flex-col p-8">
              <h3 className="font-display text-2xl font-bold">{t.corporate.title}</h3>
              <p className="mt-1 text-cream/60">{t.corporate.sub}</p>
              <ul className="mt-5 space-y-2.5 text-sm text-cream/80">
                {t.corporate.checks.map((c) => (
                  <li key={c} className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 shrink-0 text-brand" /> {c}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-7">
                <YellowBtn onClick={() => navigate("/contact")} testId="corporate-offer-btn">{t.corporate.cta}</YellowBtn>
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="flex h-full flex-col overflow-hidden rounded-3xl bg-brand text-navy">
            <img src={IMG.cateringCard} alt="Catering buffet" loading="lazy" className="h-44 w-full object-cover" />
            <div className="flex flex-1 flex-col p-8">
              <h3 className="font-display text-2xl font-bold">{t.cateringCard.title}</h3>
              <p className="mt-1 text-navy/70">{t.cateringCard.sub}</p>
              <ul className="mt-5 space-y-2.5 text-sm font-medium text-navy/85">
                {t.cateringCard.checks.map((c) => (
                  <li key={c} className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 shrink-0 text-navy" /> {c}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-7">
                <YellowBtn dark onClick={() => navigate("/contact")} testId="catering-quote-btn">{t.cateringCard.cta}</YellowBtn>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* DELIVERY AREAS + PLATFORMS */}
      <section id="order" className="border-t border-black/5 bg-paper py-16" data-testid="delivery-section">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <Reveal className="mb-8">
            <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">{t.delivery.title}</h2>
          </Reveal>
          <Reveal className="flex flex-wrap items-center gap-3">
            {t.delivery.areas.map((a) => (
              <span
                key={a}
                className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-cream px-4 py-2 text-sm font-medium text-ink/70"
                data-testid={`delivery-area-${a.toLowerCase().replace(/[^a-ząćęłńóśźż]/g, "-")}`}
              >
                <MapPin className="h-3.5 w-3.5 text-branddark" /> {a}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-brand">
              {t.delivery.city}
            </span>
          </Reveal>
          <Reveal className="mt-10" delay={0.1}>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ink/50">{t.orderVia}</p>
            <div className="flex flex-wrap gap-3">
              {t.platforms.map((p) => (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`platform-${p.id}`}
                  className="rounded-full border-2 border-navy/15 bg-cream px-6 py-2.5 font-display text-sm font-semibold text-navy transition-all hover:border-brand hover:bg-brand"
                >
                  {p.name}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
